// ============================================================================
// EXPENSE POLICY RULE BUILDER
// Visual builder for creating/editing expense policy rules
// Extends AutomationRuleBuilder pattern for expense-specific use case
// ============================================================================

'use client';

import React, { useState, useEffect, useMemo } from 'react';
import {
  Save,
  Plus,
  Trash2,
  Shield,
  AlertTriangle,
  DollarSign,
  Tag,
  CreditCard,
  Calendar,
  User,
  Loader2,
  Play,
  Copy,
  Receipt,
  Repeat,
  FileWarning,
  Users,
  FlaskConical,
  Building2,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { useExpensePolicies } from '@/hooks/useExpensePolicies';
import { simulateExpensePolicies, type PolicySimulationResult } from '@/lib/finance/expense/policyRuleEngine';
import type { ExpenseData } from '@/lib/finance/expense/policyRules';
import type { ExpensePolicyRule, ExpensePolicyCondition, ExpensePolicyActionType, ExpensePolicyRuleType } from '@/lib/finance/expense/policyRules';
import SlideOverShell from '@/components/person-detail/SlideOverShell';
import { SearchableAccountSelect } from '@/components/shared/finance/SearchableAccountSelect';
import { InlineSupplierLookup, type InlineSupplierResult } from '@/components/shared/finance/InlineSupplierLookup';
import { ALL_ALLOCATION_OPTIONS, type ExpenseAccountOption } from '@/lib/finance/expenseAccountMapping';

// ============================================================================
// TYPES
// ============================================================================

interface ExpensePolicyRuleBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (policy: ExpensePolicyRule) => void;
  editingPolicy: ExpensePolicyRule | null;
  clientId: number;
}

interface TriggerOption {
  value: ExpensePolicyRuleType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

interface ActionOption {
  value: ExpensePolicyActionType;
  label: string;
  description: string;
  icon: React.ReactNode;
  params: { key: string; label: string; type: 'text' | 'select' | 'number' | 'textarea' | 'supplier' | 'supplier_list' | 'gl_account'; options?: { value: string; label: string }[] }[];
}

const getAuthToken = (): string => {
  return authService.getTokens()?.access_token || '';
};

// ============================================================================
// TRIGGER OPTIONS (Expense Policy Rule Types)
// ============================================================================

const triggerOptions: TriggerOption[] = [
  {
    value: 'spending_limit',
    label: 'Spending Limit',
    description: 'Limit expense amount per transaction',
    icon: <DollarSign className="w-5 h-5" />,
  },
  {
    value: 'category_restriction',
    label: 'Category Restriction',
    description: 'Restrict specific expense categories',
    icon: <Tag className="w-5 h-5" />,
  },
  {
    value: 'approval_threshold',
    label: 'Approval Threshold',
    description: 'Require approval above certain amount',
    icon: <Shield className="w-5 h-5" />,
  },
  {
    value: 'vendor_policy',
    label: 'Vendor Policy',
    description: 'Restrict or flag specific vendors',
    icon: <User className="w-5 h-5" />,
  },
  {
    value: 'payment_method_policy',
    label: 'Payment Method Policy',
    description: 'Restrict payment methods',
    icon: <CreditCard className="w-5 h-5" />,
  },
  {
    value: 'daily_limit',
    label: 'Daily Limit',
    description: 'Limit total expenses per day',
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    value: 'weekly_limit',
    label: 'Weekly Limit',
    description: 'Limit total expenses per week',
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    value: 'monthly_limit',
    label: 'Monthly Limit',
    description: 'Limit total expenses per month',
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    value: 'receipt_amount_threshold',
    label: 'Receipt Amount Threshold',
    description: 'Flag expenses where receipt amount exceeds a threshold',
    icon: <Receipt className="w-5 h-5" />,
  },
  {
    value: 'duplicate_detection',
    label: 'Duplicate Detection',
    description: 'Flag potential duplicate expense submissions',
    icon: <Copy className="w-5 h-5" />,
  },
  {
    value: 'recurring_expense',
    label: 'Recurring Expense',
    description: 'Detect and flag recurring/repeated expenses',
    icon: <Repeat className="w-5 h-5" />,
  },
  {
    value: 'missing_receipt',
    label: 'Missing Receipt',
    description: 'Flag or block expenses submitted without a receipt',
    icon: <FileWarning className="w-5 h-5" />,
  },
  {
    value: 'supplier_match_policy',
    label: 'Supplier Match Policy',
    description: 'Auto-match expenses to suppliers using rules or AI',
    icon: <Building2 className="w-5 h-5" />,
  },
  {
    value: 'per_diem_policy',
    label: 'Per Diem Policy',
    description: 'Enforce rules on per diem expenses (duration, amount, location)',
    icon: <Calendar className="w-5 h-5" />,
  },
];

// ============================================================================
// SHARED VALUE OPTIONS (condition fields + action params)
// ============================================================================

// Mirrors EXPENSE_CATEGORIES in ExpenseModal — the `category` field carries the
// slug value (e.g. 'travel'), not a numeric id.
const EXPENSE_CATEGORY_OPTIONS = [
  { value: 'travel', label: 'Travel' },
  { value: 'meals', label: 'Meals & Entertainment' },
  { value: 'supplies', label: 'Office Supplies' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'software', label: 'Software & Subscriptions' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

// Mirrors EXPENSE_PAYMENT_METHOD_OPTIONS in ExpenseModal.
const EXPENSE_PAYMENT_METHOD_OPTIONS = [
  { value: 'card', label: 'Credit/Debit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'eft', label: 'EFT/Bank Transfer' },
  { value: 'petty_cash', label: 'Petty Cash' },
  { value: 'company_card', label: 'Company Card' },
];

// Values seen in expense_type across the intake paths: 'receipt' from DIN
// intake, ChildEntryType values from Smart Task captures, and the ExpenseModal
// subtype values.
const EXPENSE_TYPE_OPTIONS = [
  { value: 'receipt', label: 'Receipt (DIN / uploaded document)' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'toll', label: 'Toll' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'travel', label: 'Travel' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'supplier_invoice', label: 'Supplier Invoice' },
  { value: 'casual_wage', label: 'Casual Wage' },
  { value: 'travel_expense', label: 'Travel Expense' },
  { value: 'office_expense', label: 'Office Expense' },
  { value: 'client_expense', label: 'Client Expense' },
];

const USER_ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'system_admin', label: 'System Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'user', label: 'User' },
];

const ALLOCATION_TYPE_OPTIONS = ALL_ALLOCATION_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

// expense_subtype comes from AI suggested_category / item_subtype — open-ended,
// so the input stays free text with suggestions.
const EXPENSE_SUBTYPE_SUGGESTIONS = [
  'fuel', 'toll', 'travel', 'meals', 'accommodation', 'parking',
  'maintenance', 'mileage', 'airtime', 'data', 'office_supplies', 'other',
];

// ============================================================================
// ACTION OPTIONS
// ============================================================================

const actionOptions: ActionOption[] = [
  {
    value: 'block_submission',
    label: 'Block Submission',
    description: 'Prevent expense submission with error',
    icon: <AlertTriangle className="w-4 h-4" />,
    params: [
      { key: 'message', label: 'Error message', type: 'textarea' },
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: [
          { value: 'error', label: 'Error' },
          { value: 'warning', label: 'Warning' },
        ],
      },
    ],
  },
  {
    value: 'require_approval',
    label: 'Require Approval',
    description: 'Require approval before processing',
    icon: <Shield className="w-4 h-4" />,
    params: [
      {
        key: 'approval_level',
        label: 'Approval level',
        type: 'select',
        options: [
          { value: '1', label: 'Level 1 (Manager)' },
          { value: '2', label: 'Level 2 (Director)' },
          { value: '3', label: 'Level 3 (Executive)' },
        ],
      },
      { key: 'message', label: 'Message to user', type: 'textarea' },
    ],
  },
  {
    value: 'flag_for_review',
    label: 'Flag for Review',
    description: 'Flag expense for manual review',
    icon: <AlertTriangle className="w-4 h-4" />,
    params: [
      { key: 'message', label: 'Flag message', type: 'textarea' },
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: [
          { value: 'info', label: 'Info' },
          { value: 'warning', label: 'Warning' },
          { value: 'error', label: 'Error' },
        ],
      },
    ],
  },
  {
    value: 'auto_categorize',
    label: 'Auto-Categorize',
    description: 'Automatically assign category',
    icon: <Tag className="w-4 h-4" />,
    params: [
      { key: 'category_name', label: 'Category', type: 'select', options: EXPENSE_CATEGORY_OPTIONS },
    ],
  },
  {
    value: 'warn_user',
    label: 'Warn User',
    description: 'Show warning but allow submission',
    icon: <AlertTriangle className="w-4 h-4" />,
    params: [
      { key: 'message', label: 'Warning message', type: 'textarea' },
    ],
  },
  {
    value: 'require_vendor_registration',
    label: 'Require Vendor Registration',
    description: 'Flag expenses from unknown vendors for review or block until vendor is added to contacts',
    icon: <User className="w-4 h-4" />,
    params: [
      { key: 'message', label: 'Message to user', type: 'textarea' },
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: [
          { value: 'warning', label: 'Warning — allow submission' },
          { value: 'error', label: 'Block — require vendor registration' },
        ],
      },
      {
        key: 'auto_create_vendor',
        label: 'Auto-create vendor contact',
        type: 'select',
        options: [
          { value: 'false', label: 'No — flag for manual review' },
          { value: 'true', label: 'Yes — create draft contact automatically' },
        ],
      },
    ],
  },
  {
    value: 'auto_match_supplier',
    label: 'Auto-Match Supplier',
    description: 'Match expense vendor to a known supplier using rules, vendor memory, or AI',
    icon: <Building2 className="w-4 h-4" />,
    params: [
      {
        key: 'match_strategy',
        label: 'Match strategy',
        type: 'select',
        options: [
          { value: 'rules_first', label: 'Rules first, then AI fallback' },
          { value: 'ai_first', label: 'AI first, then rules fallback' },
          { value: 'rules_only', label: 'Rules only (no AI)' },
          { value: 'ai_only', label: 'AI only (no rules)' },
        ],
      },
      {
        key: 'auto_accept_threshold',
        label: 'Auto-accept confidence threshold (%)',
        type: 'number',
      },
      {
        key: 'fallback_action',
        label: 'If no match found',
        type: 'select',
        options: [
          { value: 'flag_for_review', label: 'Flag for manual review' },
          { value: 'create_contact', label: 'Auto-create new contact' },
          { value: 'ignore', label: 'Ignore (no action)' },
        ],
      },
    ],
  },
  {
    value: 'auto_assign_supplier',
    label: 'Auto-Assign Supplier',
    description: 'Always assign a specific supplier contact based on conditions (e.g. fuel → Shell)',
    icon: <Building2 className="w-4 h-4" />,
    params: [
      { key: 'contact_id', label: 'Supplier contact', type: 'supplier' },
      { key: 'account_code', label: 'Default GL account (optional)', type: 'gl_account' },
      { key: 'message', label: 'Note (optional)', type: 'textarea' },
    ],
  },
  {
    value: 'auto_approve',
    label: 'Auto-Approve',
    description: 'Automatically approve and process matching documents without manual review (Dext-style straight-through processing)',
    icon: <Shield className="w-4 h-4" />,
    params: [
      { key: 'max_amount', label: 'Maximum amount (in the expense currency — add a Currency condition to scope)', type: 'number' },
      { key: 'ai_confidence_threshold', label: 'Minimum match/categorization confidence (%)', type: 'number' },
      { key: 'whitelisted_supplier_ids', label: 'Restrict to suppliers (optional)', type: 'supplier_list' },
      {
        key: 'require_receipt',
        label: 'Require receipt attached',
        type: 'select',
        options: [
          { value: 'true', label: 'Yes — only auto-approve documents with a receipt' },
          { value: 'false', label: 'No' },
        ],
      },
      {
        key: 'recurring_only',
        label: 'Recurring expenses only',
        type: 'select',
        options: [
          { value: 'false', label: 'No — apply to all matching expenses' },
          { value: 'true', label: 'Yes — only auto-approve recurring expenses' },
        ],
      },
      { key: 'message', label: 'Audit note (optional)', type: 'textarea' },
    ],
  },
];

/** Supplier match rule authored in the builder (match_field/match_value → assign contact). */
interface SupplierRuleEntry {
  id: string;
  rule_name: string;
  match_field: 'vendor_name' | 'vat_number' | 'email_domain' | 'reference_pattern';
  operator: 'equals' | 'contains' | 'starts_with' | 'ends_with' | 'regex';
  match_value: string;
  case_sensitive: boolean;
  contact_id: string;
  contact_name: string;
  account_code: string;
  auto_categorize: boolean;
  priority: number;
}

// ============================================================================
// CONDITION FIELD OPTIONS
// ============================================================================

const conditionFields = [
  { value: 'amount', label: 'Amount' },
  { value: 'currency', label: 'Currency' },
  { value: 'category', label: 'Category' },
  { value: 'category_id', label: 'Category ID' },
  { value: 'vendor', label: 'Supplier Name (extracted text)' },
  { value: 'vendor_id', label: 'Supplier (vendor_id)' },
  { value: 'supplier_id', label: 'Supplier' },
  { value: 'supplier_vat', label: 'Supplier VAT Number' },
  { value: 'supplier_email_domain', label: 'Supplier Email Domain' },
  { value: 'payment_method', label: 'Payment Method' },
  { value: 'expense_date', label: 'Expense Date' },
  { value: 'user_id', label: 'User ID' },
  { value: 'user_role', label: 'User Role' },
  { value: 'expense_type', label: 'Expense Type' },
  { value: 'expense_subtype', label: 'Expense Subtype (fuel, toll, travel, etc.)' },
  { value: 'receipt_amount', label: 'Receipt Amount' },
  { value: 'has_receipt', label: 'Has Receipt' },
  { value: 'receipt_number', label: 'Receipt Number' },
  { value: 'per_diem_days', label: 'Per Diem Days' },
  { value: 'per_diem_amount', label: 'Per Diem Amount' },
  { value: 'per_diem_location', label: 'Per Diem Location' },
  { value: 'per_diem_trip_duration', label: 'Per Diem Trip Duration (days)' },
  { value: 'line_gl_account', label: 'Line Item GL Account' },
  { value: 'line_allocation_type', label: 'Line Item Allocation Type (job, general, etc.)' },
  { value: 'line_quantity', label: 'Line Item Quantity' },
  { value: 'line_is_billable', label: 'Line Item Is Billable' },
  { value: 'line_total', label: 'Line Item Total' },
];

const conditionOperators = [
  { value: '=', label: 'Equals' },
  { value: '!=', label: 'Not Equals' },
  { value: '>', label: 'Greater Than' },
  { value: '<', label: 'Less Than' },
  { value: '>=', label: 'Greater or Equal' },
  { value: '<=', label: 'Less or Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In List' },
  { value: 'not_in', label: 'Not In List' },
  { value: 'between', label: 'Between' },
];

// ============================================================================
// CONDITION VALUE INPUT KINDS
// ============================================================================

type ConditionValueKind =
  | 'amount' | 'number' | 'date' | 'boolean' | 'select'
  | 'currency' | 'gl_account' | 'supplier_name' | 'supplier_id' | 'text';

interface ConditionFieldMeta {
  kind: ConditionValueKind;
  options?: { value: string; label: string }[];
  datalist?: string[];
  /** Operators valid for this field — the operator select is filtered to these. */
  operators: ExpensePolicyCondition['operator'][];
}

const NUMERIC_OPS: ExpensePolicyCondition['operator'][] = ['=', '!=', '>', '<', '>=', '<=', 'in', 'not_in', 'between'];
const DATE_OPS: ExpensePolicyCondition['operator'][] = ['=', '!=', '>', '<', '>=', '<=', 'between'];
const EXACT_OPS: ExpensePolicyCondition['operator'][] = ['=', '!=', 'in', 'not_in'];
const BOOLEAN_OPS: ExpensePolicyCondition['operator'][] = ['=', '!='];
const TEXT_OPS: ExpensePolicyCondition['operator'][] = ['=', '!=', 'contains', 'in', 'not_in'];

const CONDITION_FIELD_META: Record<string, ConditionFieldMeta> = {
  amount: { kind: 'amount', operators: NUMERIC_OPS },
  currency: { kind: 'currency', operators: EXACT_OPS },
  category: { kind: 'select', options: EXPENSE_CATEGORY_OPTIONS, operators: EXACT_OPS },
  category_id: { kind: 'number', operators: NUMERIC_OPS },
  // `vendor` is matched against the vendor name extracted from the document —
  // '='/'in' pick a supplier (display name stored); 'contains' is free text
  // since it intentionally matches arbitrary OCR output.
  vendor: { kind: 'supplier_name', operators: TEXT_OPS },
  vendor_id: { kind: 'supplier_id', operators: EXACT_OPS },
  supplier_id: { kind: 'supplier_id', operators: EXACT_OPS },
  supplier_vat: { kind: 'text', operators: TEXT_OPS },
  supplier_email_domain: { kind: 'text', operators: TEXT_OPS },
  payment_method: { kind: 'select', options: EXPENSE_PAYMENT_METHOD_OPTIONS, operators: EXACT_OPS },
  expense_date: { kind: 'date', operators: DATE_OPS },
  user_id: { kind: 'number', operators: NUMERIC_OPS },
  user_role: { kind: 'select', options: USER_ROLE_OPTIONS, operators: EXACT_OPS },
  expense_type: { kind: 'select', options: EXPENSE_TYPE_OPTIONS, operators: EXACT_OPS },
  expense_subtype: { kind: 'text', datalist: EXPENSE_SUBTYPE_SUGGESTIONS, operators: TEXT_OPS },
  receipt_amount: { kind: 'amount', operators: NUMERIC_OPS },
  has_receipt: { kind: 'boolean', operators: BOOLEAN_OPS },
  receipt_number: { kind: 'text', operators: TEXT_OPS },
  per_diem_days: { kind: 'number', operators: NUMERIC_OPS },
  per_diem_amount: { kind: 'amount', operators: NUMERIC_OPS },
  per_diem_location: { kind: 'text', operators: TEXT_OPS },
  per_diem_trip_duration: { kind: 'number', operators: NUMERIC_OPS },
  line_gl_account: { kind: 'gl_account', operators: EXACT_OPS },
  line_allocation_type: { kind: 'select', options: ALLOCATION_TYPE_OPTIONS, operators: EXACT_OPS },
  line_quantity: { kind: 'number', operators: NUMERIC_OPS },
  line_is_billable: { kind: 'boolean', operators: BOOLEAN_OPS },
  line_total: { kind: 'amount', operators: NUMERIC_OPS },
};

const DEFAULT_FIELD_META: ConditionFieldMeta = { kind: 'text', operators: TEXT_OPS };

function fieldMeta(field: string): ConditionFieldMeta {
  return CONDITION_FIELD_META[field] ?? DEFAULT_FIELD_META;
}

function defaultValueForKind(kind: ConditionValueKind): ExpensePolicyCondition['value'] {
  if (kind === 'boolean') return 'true';
  if (kind === 'amount' || kind === 'number') return 0;
  return '';
}

// ============================================================================
// CONDITION VALUE INPUT (per-field typed editor)
// ============================================================================

interface ConditionValueInputProps {
  cond: ExpensePolicyCondition;
  onChange: (value: ExpensePolicyCondition['value']) => void;
  isDark: boolean;
  expenseAccounts: ExpenseAccountOption[];
  currencyOptions: { value: string; label: string }[];
  /** Resolve a stored supplier contact id to a display name. */
  supplierNameFor: (id: string) => string | undefined;
}

function ConditionValueInput({
  cond,
  onChange,
  isDark,
  expenseAccounts,
  currencyOptions,
  supplierNameFor,
}: ConditionValueInputProps) {
  const meta = fieldMeta(cond.field);
  const inputCls = `w-full px-2 py-1 rounded border text-sm ${isDark
    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
    }`;
  const chipCls = `inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`;

  const asList = (): string[] =>
    Array.isArray(cond.value)
      ? cond.value.map(String)
      : cond.value === '' || cond.value == null
        ? []
        : [String(cond.value)];

  const displayFor = (v: string): string => {
    if (meta.kind === 'supplier_id' || meta.kind === 'supplier_name') return supplierNameFor(v) ?? v;
    if (meta.kind === 'select') return meta.options?.find((o) => o.value === v)?.label ?? v;
    if (meta.kind === 'currency') return currencyOptions.find((o) => o.value === v)?.label ?? v;
    return v;
  };

  const chips = (values: string[], removeAt: (i: number) => void) => (
    <div className="flex flex-wrap gap-1">
      {values.map((v, i) => (
        <span key={`${v}-${i}`} className={chipCls}>
          {displayFor(v)}
          <button type="button" onClick={() => removeAt(i)} className="hover:text-red-500">×</button>
        </span>
      ))}
    </div>
  );

  const pushToList = (v: string | number | undefined | null) => {
    const s = String(v ?? '').trim();
    if (!s) return;
    const values = asList();
    if (values.includes(s)) return;
    onChange([...values, meta.kind === 'amount' || meta.kind === 'number' ? Number(s) : s]);
  };

  // --- in / not_in → multi-value chip editor -------------------------------
  if (cond.operator === 'in' || cond.operator === 'not_in') {
    const values = asList();
    return (
      <div className="flex-1 min-w-0 space-y-1.5">
        {values.length > 0 && chips(values, (i) => onChange(values.filter((_, idx) => idx !== i)))}
        {meta.kind === 'gl_account' ? (
          <SearchableAccountSelect
            accounts={expenseAccounts}
            value=""
            onChange={(code) => pushToList(code)}
            placeholder="Add GL account…"
            isDark={isDark}
          />
        ) : meta.kind === 'supplier_id' || meta.kind === 'supplier_name' ? (
          <InlineSupplierLookup
            value={null}
            onChange={(s) => pushToList(meta.kind === 'supplier_id' ? s?.id : s?.display_name)}
            isDark={isDark}
            placeholder="Add supplier…"
          />
        ) : meta.kind === 'select' || meta.kind === 'currency' || meta.kind === 'boolean' ? (
          <select
            value=""
            onChange={(e) => pushToList(e.target.value)}
            className={inputCls}
          >
            <option value="">Add value…</option>
            {(meta.kind === 'currency' ? currencyOptions : meta.kind === 'boolean' ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] : meta.options || [])
              .filter((o) => !values.includes(o.value))
              .map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
          </select>
        ) : (
          <TextAdder
            inputCls={inputCls}
            isDate={meta.kind === 'date'}
            isNumeric={meta.kind === 'amount' || meta.kind === 'number'}
            datalist={meta.datalist}
            onAdd={pushToList}
          />
        )}
      </div>
    );
  }

  // --- between → two-value editor -------------------------------------------
  if (cond.operator === 'between' && (meta.kind === 'amount' || meta.kind === 'number' || meta.kind === 'date')) {
    const pair: (string | number)[] = Array.isArray(cond.value) ? cond.value : [cond.value ?? '', ''];
    const setPair = (i: 0 | 1, v: string) =>
      onChange(meta.kind === 'date'
        ? (i === 0 ? [v, pair[1] ?? ''] : [pair[0] ?? '', v])
        : [i === 0 ? (v === '' ? '' : Number(v)) : pair[0] ?? '', i === 1 ? (v === '' ? '' : Number(v)) : pair[1] ?? '']);
    const pairInputCls = `flex-1 min-w-0 px-2 py-1 rounded border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
    return (
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <input
          type={meta.kind === 'date' ? 'date' : 'number'}
          step={meta.kind === 'amount' ? '0.01' : undefined}
          min={meta.kind === 'date' ? undefined : '0'}
          value={pair[0] ?? ''}
          onChange={(e) => setPair(0, e.target.value)}
          placeholder="Min"
          className={pairInputCls}
        />
        <span className={`text-xs shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>to</span>
        <input
          type={meta.kind === 'date' ? 'date' : 'number'}
          step={meta.kind === 'amount' ? '0.01' : undefined}
          min={meta.kind === 'date' ? undefined : '0'}
          value={pair[1] ?? ''}
          onChange={(e) => setPair(1, e.target.value)}
          placeholder="Max"
          className={pairInputCls}
        />
      </div>
    );
  }

  // --- scalar editors ---------------------------------------------------------
  switch (meta.kind) {
    case 'amount':
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          value={cond.value === '' || cond.value == null ? '' : String(cond.value)}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder="0.00"
          className={inputCls}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={cond.value === '' || cond.value == null ? '' : String(cond.value)}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className={inputCls}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={String(cond.value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      );
    case 'boolean':
      return (
        <select
          value={String(cond.value ?? 'true')}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    case 'currency':
      return (
        <select
          value={String(cond.value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        >
          <option value="">Select currency…</option>
          {currencyOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    case 'select':
      return (
        <select
          value={String(cond.value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        >
          <option value="">Select…</option>
          {(meta.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    case 'gl_account':
      return (
        <SearchableAccountSelect
          accounts={expenseAccounts}
          value={String(cond.value ?? '')}
          onChange={(code) => onChange(code)}
          placeholder="— GL account —"
          isDark={isDark}
        />
      );
    case 'supplier_name':
    case 'supplier_id': {
      // 'contains' on the extracted vendor name is arbitrary OCR-text matching —
      // supplier lookup only for exact comparisons.
      if (meta.kind === 'supplier_name' && cond.operator === 'contains') {
        return (
          <input
            type="text"
            value={String(cond.value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Text contained in extracted vendor name"
            className={inputCls}
          />
        );
      }
      const stored = String(cond.value ?? '');
      const selected: InlineSupplierResult | null = stored
        ? {
          id: stored,
          display_name:
            meta.kind === 'supplier_name' ? stored : supplierNameFor(stored) ?? stored,
        }
        : null;
      return (
        <InlineSupplierLookup
          value={selected}
          onChange={(s) => onChange(meta.kind === 'supplier_id' ? (s?.id ?? '') : (s?.display_name ?? ''))}
          isDark={isDark}
          placeholder="Search suppliers…"
        />
      );
    }
    default:
      return (
        <>
          <input
            type="text"
            value={String(cond.value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Value"
            list={meta.datalist ? 'expense-subtype-suggestions' : undefined}
            className={inputCls}
          />
          {meta.datalist && (
            <datalist id="expense-subtype-suggestions">
              {meta.datalist.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </>
      );
  }
}

/** Small text/number/date input that commits a value on Enter or blur-Add. */
function TextAdder({
  inputCls,
  onAdd,
  isDate,
  isNumeric,
  datalist,
}: {
  inputCls: string;
  onAdd: (v: string) => void;
  isDate?: boolean;
  isNumeric?: boolean;
  datalist?: string[];
}) {
  const [draft, setDraft] = useState('');
  const listId = datalist ? `multi-${datalist[0]}-${datalist.length}` : undefined;
  return (
    <div className="flex items-center gap-1.5">
      <input
        type={isDate ? 'date' : isNumeric ? 'number' : 'text'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft.trim()) {
            e.preventDefault();
            onAdd(draft.trim());
            setDraft('');
          }
        }}
        placeholder="Add value…"
        list={listId}
        className={inputCls}
      />
      {listId && (
        <datalist id={listId}>
          {datalist!.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
      <button
        type="button"
        onClick={() => {
          if (draft.trim()) {
            onAdd(draft.trim());
            setDraft('');
          }
        }}
        className={`shrink-0 px-2 py-1 rounded border text-xs ${'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
      >
        Add
      </button>
    </div>
  );
}

/** Chip editor for a list of supplier contact ids (auto_approve whitelist). */
function SupplierIdListEditor({
  value,
  onChange,
  isDark,
  supplierNameFor,
}: {
  value: unknown;
  onChange: (ids: string[]) => void;
  isDark: boolean;
  supplierNameFor: (id: string) => string | undefined;
}) {
  const ids: string[] = Array.isArray(value)
    ? value.map(String).filter(Boolean)
    : String(value || '').split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-1.5">
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {ids.map((id) => (
            <span
              key={id}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}
            >
              {supplierNameFor(id) ?? id}
              <button
                type="button"
                onClick={() => onChange(ids.filter((x) => x !== id))}
                className="hover:text-red-500"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <InlineSupplierLookup
        value={null}
        onChange={(s) => {
          if (s && !ids.includes(s.id)) onChange([...ids, s.id]);
        }}
        isDark={isDark}
        placeholder="Add supplier…"
      />
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ExpensePolicyRuleBuilder({
  isOpen,
  onClose,
  onSave,
  editingPolicy,
  clientId,
}: ExpensePolicyRuleBuilderProps) {
  const { currentTheme, actionColor } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  // Theme-branded focus ring — Tailwind draws `focus:ring-*` from
  // --tw-ring-color, so set the CSS var inline instead of a static ring class.
  const ringFocusStyle = { '--tw-ring-color': actionColor } as React.CSSProperties;

  // Form state
  const [ruleName, setRuleName] = useState('');
  const [ruleType, setRuleType] = useState<ExpensePolicyRuleType>('spending_limit');
  const [priority, setPriority] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [conditions, setConditions] = useState<{ all: ExpensePolicyCondition[]; any: ExpensePolicyCondition[] }>({
    all: [],
    any: [],
  });
  const [actionType, setActionType] = useState<ExpensePolicyActionType>('block_submission');
  const [actionConfig, setActionConfig] = useState<ExpensePolicyRule['action_config']>({});

  // Targeting state
  const [appliesToAll, setAppliesToAll] = useState(true);
  const [targetRoles, setTargetRoles] = useState<string>('');
  const [targetUserIds, setTargetUserIds] = useState<string>('');

  // Test/simulate state
  const { policies: existingPolicies } = useExpensePolicies(true);
  const [simResults, setSimResults] = useState<PolicySimulationResult[] | null>(null);
  const [simRunning, setSimRunning] = useState(false);
  const [simData, setSimData] = useState<ExpenseData>({
    amount: 0,
    currency: 'ZAR',
    category: '',
    vendor: '',
    vendor_id: undefined,
    supplier_id: '',
    payment_method: '',
    expense_date: new Date().toISOString().split('T')[0],
    expense_type: '',
    expense_subtype: '',
    has_receipt: true,
    receipt_amount: 0,
    receipt_number: '',
  });

  // Supplier rules state (for supplier_match_policy type)
  const [supplierRules, setSupplierRules] = useState<SupplierRuleEntry[]>([]);
  const [contacts, setContacts] = useState<{ id: string; name: string }[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<ExpenseAccountOption[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<{ value: string; label: string }[]>([]);

  // Fetch supplier contacts (for id→name display), GL accounts, and currencies
  // whenever the builder opens.
  useEffect(() => {
    if (!isOpen) return;
    const token = getAuthToken();
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}` };

    const fetchContacts = async () => {
      try {
        const response = await fetch('/api/v1/contacts?limit=200&type=supplier', { headers });
        if (response.ok) {
          const data = await response.json();
          const list = (data.data || data.contacts || []).map((c: { id: string | number; company_name?: string | null; contact_person?: string | null; name?: string | null }) => ({
            id: String(c.id),
            name: c.company_name || c.contact_person || c.name || 'Unknown',
          }));
          setContacts(list);
        }
      } catch (e) {
        console.error('Error fetching contacts:', e);
      }
    };

    const fetchExpenseAccounts = async () => {
      try {
        const response = await fetch('/api/v1/chart-of-accounts', { headers });
        if (response.ok) {
          const result = await response.json();
          const accounts: ExpenseAccountOption[] = Array.isArray(result?.data)
            ? result.data
              .filter((account: unknown): account is ExpenseAccountOption => Boolean(
                account
                && typeof account === 'object'
                && 'account_code' in account
                && 'name' in account
              ))
              .map((account: ExpenseAccountOption) => ({
                account_code: String(account.account_code || '').trim(),
                name: String(account.name || '').trim(),
                account_type: account.account_type ?? null,
                account_subtype: account.account_subtype ?? null,
                allowed_allocation_types: account.allowed_allocation_types ?? null,
              }))
            : [];
          setExpenseAccounts(accounts);
        }
      } catch (e) {
        console.error('Error fetching expense accounts:', e);
      }
    };

    const fetchCurrencies = async () => {
      try {
        const response = await fetch('/api/v1/finance/fx/currencies', { headers });
        if (response.ok) {
          const result = await response.json();
          const list = Array.isArray(result?.data) ? result.data : [];
          setCurrencyOptions(
            list
              .filter((c: { code?: string; is_active?: boolean }) => c.code && c.is_active !== false)
              .map((c: { code: string; name?: string }) => ({
                value: c.code,
                label: c.name ? `${c.code} — ${c.name}` : c.code,
              })),
          );
        }
      } catch (e) {
        console.error('Error fetching currencies:', e);
      }
    };

    fetchContacts();
    fetchExpenseAccounts();
    fetchCurrencies();
  }, [isOpen]);

  /** Resolve a stored supplier contact id to a display name. */
  const supplierNameFor = (id: string | number | undefined | null) =>
    contacts.find((c) => c.id === String(id))?.name;

  // Initialize supplier rules from editing policy
  useEffect(() => {
    if (editingPolicy) {
      setSupplierRules((editingPolicy.supplier_rules || []) as SupplierRuleEntry[]);
    } else {
      setSupplierRules([]);
    }
  }, [editingPolicy, isOpen]);

  const addSupplierRule = () => {
    setSupplierRules([...supplierRules, {
      id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      rule_name: '',
      match_field: 'vendor_name',
      operator: 'contains',
      match_value: '',
      case_sensitive: false,
      contact_id: '',
      contact_name: '',
      account_code: '',
      auto_categorize: false,
      priority: 10,
    }]);
  };

  const updateSupplierRule = (index: number, updates: Partial<typeof supplierRules[0]>) => {
    const newRules = [...supplierRules];
    newRules[index] = { ...newRules[index], ...updates };
    if (updates.contact_id && contacts.length > 0) {
      const contact = contacts.find(c => c.id === updates.contact_id);
      if (contact) newRules[index].contact_name = contact.name;
    }
    setSupplierRules(newRules);
  };

  const removeSupplierRule = (index: number) => {
    setSupplierRules(supplierRules.filter((_, i) => i !== index));
  };

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'trigger' | 'conditions' | 'actions' | 'settings' | 'test'>('trigger');

  // Initialize form when editing
  useEffect(() => {
    if (editingPolicy) {
      setRuleName(editingPolicy.rule_name);
      setRuleType(editingPolicy.rule_type);
      setPriority(editingPolicy.priority);
      setIsActive(editingPolicy.is_active);
      setConditions({
        all: editingPolicy.conditions?.all ?? [],
        any: editingPolicy.conditions?.any ?? [],
      });
      setActionType(editingPolicy.action_type);
      setActionConfig(editingPolicy.action_config || {});
      setAppliesToAll(editingPolicy.applies_to_all !== false);
      setTargetRoles((editingPolicy.target_roles || []).join(', '));
      setTargetUserIds((editingPolicy.target_user_ids || []).join(', '));
      setStep('trigger');
    } else {
      // Reset form for a new rule.
      setRuleName('');
      setRuleType('spending_limit');
      setPriority(0);
      setIsActive(true);
      setConditions({ all: [], any: [] });
      setActionType('block_submission');
      setActionConfig({});
      setAppliesToAll(true);
      setTargetRoles('');
      setTargetUserIds('');
      setStep('trigger');
    }
    setError('');
  }, [editingPolicy, isOpen]);

  const addCondition = (type: 'all' | 'any') => {
    setConditions((prev) => ({
      ...prev,
      [type]: [
        ...prev[type],
        {
          field: 'amount',
          operator: '>',
          value: 0,
        },
      ],
    }));
  };

  const updateCondition = (
    type: 'all' | 'any',
    index: number,
    field: keyof ExpensePolicyCondition,
    value: ExpensePolicyCondition[keyof ExpensePolicyCondition]
  ) => {
    setConditions((prev) => ({
      ...prev,
      [type]: prev[type].map((cond, i) => {
        if (i !== index) return cond;

        // Field change → reset operator/value to something valid for the new
        // field's input kind (e.g. 'between' makes no sense on has_receipt).
        if (field === 'field') {
          const meta = fieldMeta(String(value));
          const next: ExpensePolicyCondition = { ...cond, field: value as ExpensePolicyCondition['field'] };
          if (!meta.operators.includes(next.operator)) next.operator = meta.operators[0];
          next.value = defaultValueForKind(meta.kind);
          return next;
        }

        // Operator change → reshape the stored value: 'between' holds
        // [min, max]; 'in'/'not_in' hold arrays; scalars hold one value.
        if (field === 'operator') {
          const next: ExpensePolicyCondition = { ...cond, operator: value as ExpensePolicyCondition['operator'] };
          const arr = Array.isArray(cond.value)
            ? cond.value
            : cond.value === '' || cond.value == null
              ? []
              : [cond.value];
          if (value === 'between') {
            next.value = [arr[0] ?? defaultValueForKind(fieldMeta(cond.field).kind), arr[1] ?? ''];
          } else if (value === 'in' || value === 'not_in') {
            next.value = arr;
          } else {
            next.value = arr[0] ?? defaultValueForKind(fieldMeta(cond.field).kind);
          }
          return next;
        }

        return { ...cond, [field]: value };
      }),
    }));
  };

  const removeCondition = (type: 'all' | 'any', index: number) => {
    setConditions((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const renderCondition = (type: 'all' | 'any', cond: ExpensePolicyCondition, index: number) => {
    const meta = fieldMeta(cond.field);
    const selectCls = `px-2 py-1 rounded border text-sm ${isDark
      ? 'bg-gray-700 border-gray-600 text-white'
      : 'bg-white border-gray-300 text-gray-900'
      }`;
    return (
      <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <select
          value={cond.field}
          onChange={(e) => updateCondition(type, index, 'field', e.target.value)}
          className={`w-48 shrink-0 ${selectCls}`}
        >
          {conditionFields.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          value={cond.operator}
          onChange={(e) => updateCondition(type, index, 'operator', e.target.value)}
          className={`shrink-0 ${selectCls}`}
        >
          {conditionOperators
            .filter((op) => meta.operators.includes(op.value as ExpensePolicyCondition['operator']))
            .map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
        </select>
        <div className="flex-1 min-w-0">
          <ConditionValueInput
            cond={cond}
            onChange={(v) => updateCondition(type, index, 'value', v)}
            isDark={isDark}
            expenseAccounts={expenseAccounts}
            currencyOptions={currencyOptions}
            supplierNameFor={supplierNameFor}
          />
          {meta.kind === 'amount' && (
            <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Evaluated in the expense&apos;s own currency — add a Currency condition to scope this rule.
            </p>
          )}
        </div>
        <button
          onClick={() => removeCondition(type, index)}
          className="p-1 text-red-600 hover:text-red-700"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const runSimulation = () => {
    setSimRunning(true);
    setSimResults(null);
    try {
      const draftPolicy: ExpensePolicyRule = {
        id: 'draft',
        client_id: clientId,
        rule_name: ruleName || 'Draft Policy',
        rule_type: ruleType,
        priority,
        is_active: true,
        conditions,
        action_type: actionType,
        action_config: actionConfig,
        applies_to_all: appliesToAll,
        target_roles: appliesToAll ? undefined : targetRoles.split(',').map((r) => r.trim()).filter(Boolean),
        target_user_ids: appliesToAll ? undefined : targetUserIds.split(',').map((u) => Number(u.trim())).filter((u) => !isNaN(u) && u > 0),
        times_applied: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const allPolicies = [...existingPolicies, draftPolicy];
      const results = simulateExpensePolicies(allPolicies, simData, { clientId });
      setSimResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Simulation failed');
    } finally {
      setSimRunning(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!ruleName.trim()) {
      setError('Please enter a rule name');
      return;
    }
    if (conditions.all.length === 0 && conditions.any.length === 0) {
      setError('Please add at least one condition');
      setStep('conditions');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const policy: ExpensePolicyRule = {
        id: editingPolicy?.id || crypto.randomUUID(),
        client_id: clientId,
        rule_name: ruleName.trim(),
        rule_type: ruleType,
        priority,
        is_active: isActive,
        conditions,
        action_type: actionType,
        action_config: actionConfig,
        applies_to_all: appliesToAll,
        target_roles: appliesToAll ? undefined : targetRoles.split(',').map((r) => r.trim()).filter(Boolean),
        target_user_ids: appliesToAll ? undefined : targetUserIds.split(',').map((u) => Number(u.trim())).filter((u) => !isNaN(u) && u > 0),
        supplier_rules: ruleType === 'supplier_match_policy' && supplierRules.length > 0 ? supplierRules : undefined,
        times_applied: editingPolicy?.times_applied || 0,
        last_applied_at: editingPolicy?.last_applied_at,
        created_by: editingPolicy?.created_by,
        created_at: editingPolicy?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      onSave(policy);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectedTrigger = triggerOptions.find((t) => t.value === ruleType);
  const selectedAction = actionOptions.find((a) => a.value === actionType);

  return (
    <SlideOverShell
      onClose={onClose}
      icon={
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${actionColor}15` }}>
          <Shield className="w-5 h-5" style={{ color: actionColor }} />
        </div>
      }
      title={editingPolicy ? 'Edit Expense Policy' : 'New Expense Policy'}
      subtitle={
        selectedTrigger ? (
          <span className="text-[10px] text-gray-500 dark:text-gray-400">{selectedTrigger.label}</span>
        ) : undefined
      }
      tabs={[
        { id: 'trigger', label: '1. Trigger' },
        { id: 'conditions', label: '2. Conditions' },
        { id: 'actions', label: '3. Actions' },
        { id: 'settings', label: '4. Settings' },
        { id: 'test', label: '5. Test' },
      ]}
      activeTab={step}
      onTabChange={(id) => setStep(id as typeof step)}
      footer={
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {step !== 'trigger' && (
              <button
                onClick={() => setStep(step === 'test' ? 'settings' : step === 'settings' ? 'actions' : step === 'actions' ? 'conditions' : 'trigger')}
                className={`px-4 py-2 rounded-lg border ${isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } transition-colors`}
              >
                Back
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg border ${isDark
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                } transition-colors`}
            >
              Cancel
            </button>
            {step !== 'settings' && step !== 'test' ? (
              <button
                onClick={() => setStep(step === 'trigger' ? 'conditions' : step === 'conditions' ? 'actions' : 'settings')}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: actionColor }}
              >
                Next
              </button>
            ) : step === 'settings' ? (
              <button
                onClick={() => setStep('test')}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: actionColor }}
              >
                <FlaskConical className="w-4 h-4" />
                Test
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: actionColor }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save Policy'}
              </button>
            )}
          </div>
        </div>
      }
    >
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Trigger */}
          {step === 'trigger' && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Policy Name *
                </label>
                <input
                  type="text"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="e.g., Block expenses over $500"
                  className={`w-full px-3 py-2 rounded-lg border ${isDark
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2`}
                  style={ringFocusStyle}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Policy Type *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {triggerOptions.map((trigger) => (
                    <button
                      key={trigger.value}
                      type="button"
                      onClick={() => setRuleType(trigger.value)}
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all ${ruleType === trigger.value
                          ? 'border-transparent'
                          : isDark
                            ? 'border-gray-600 hover:border-gray-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      style={ruleType === trigger.value ? { borderColor: actionColor, backgroundColor: `${actionColor}14` } : undefined}
                    >
                      <div
                        className={`p-1.5 rounded ${ruleType === trigger.value
                            ? ''
                            : isDark
                              ? 'bg-gray-700 text-gray-400'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        style={ruleType === trigger.value ? { backgroundColor: `${actionColor}15`, color: actionColor } : undefined}
                      >
                        {trigger.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium ${ruleType === trigger.value
                              ? ''
                              : isDark
                                ? 'text-gray-200'
                                : 'text-gray-700'
                            }`}
                          style={ruleType === trigger.value ? { color: actionColor } : undefined}
                        >
                          {trigger.label}
                        </div>
                        <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {trigger.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Conditions */}
          {step === 'conditions' && (
            <div className="space-y-6">
              {/* Supplier Rules Section (only for supplier_match_policy) */}
              {ruleType === 'supplier_match_policy' && (
                <div className="p-4 rounded-lg border" style={{ backgroundColor: `${actionColor}0D`, borderColor: `${actionColor}40` }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" style={{ color: actionColor }} />
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Supplier Matching Rules
                      </label>
                    </div>
                    <button
                      onClick={addSupplierRule}
                      className="flex items-center gap-1 text-sm hover:opacity-80"
                      style={{ color: actionColor }}
                    >
                      <Plus className="w-4 h-4" />
                      Add rule
                    </button>
                  </div>
                  <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Define rules to auto-match vendors to suppliers. Rules are evaluated by priority (highest first). These rules complement the conditions below.
                  </p>

                  {supplierRules.length === 0 ? (
                    <div className={`text-center py-4 rounded-lg border-2 border-dashed ${
                      isDark ? 'border-gray-600' : 'border-gray-300'
                    }`}>
                      <Building2 className={`w-6 h-6 mx-auto mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        No supplier rules yet. Add a rule to auto-match vendors.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {supplierRules.map((rule, index) => (
                        <div key={rule.id} className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              value={rule.rule_name}
                              onChange={(e) => updateSupplierRule(index, { rule_name: e.target.value })}
                              placeholder="Rule name"
                              className={`flex-1 px-2 py-1 rounded border text-xs ${
                                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                            <button
                              onClick={() => removeSupplierRule(index)}
                              className="p-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <select
                              value={rule.match_field}
                              onChange={(e) => updateSupplierRule(index, { match_field: e.target.value as SupplierRuleEntry['match_field'] })}
                              className={`px-2 py-1 rounded border text-xs ${
                                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              <option value="vendor_name">Vendor name</option>
                              <option value="vat_number">VAT number</option>
                              <option value="email_domain">Email domain</option>
                              <option value="reference_pattern">Reference</option>
                            </select>
                            <select
                              value={rule.operator}
                              onChange={(e) => updateSupplierRule(index, { operator: e.target.value as SupplierRuleEntry['operator'] })}
                              className={`px-2 py-1 rounded border text-xs ${
                                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              <option value="equals">Equals</option>
                              <option value="contains">Contains</option>
                              <option value="starts_with">Starts with</option>
                              <option value="ends_with">Ends with</option>
                              <option value="regex">Regex</option>
                            </select>
                            <input
                              type="text"
                              value={rule.match_value}
                              onChange={(e) => updateSupplierRule(index, { match_value: e.target.value })}
                              placeholder="Match value"
                              className={`px-2 py-1 rounded border text-xs ${
                                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <label className={`block text-[10px] mb-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Assign to supplier
                              </label>
                              <InlineSupplierLookup
                                value={rule.contact_id
                                  ? { id: rule.contact_id, display_name: rule.contact_name || supplierNameFor(rule.contact_id) || rule.contact_id }
                                  : null}
                                onChange={(s) => updateSupplierRule(index, {
                                  contact_id: s?.id || '',
                                  contact_name: s?.display_name || '',
                                })}
                                isDark={isDark}
                                placeholder="Search suppliers…"
                              />
                            </div>
                            <div>
                              <label className={`block text-[10px] mb-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                GL account
                              </label>
                              <SearchableAccountSelect
                                accounts={expenseAccounts}
                                value={rule.account_code}
                                onChange={(code) => updateSupplierRule(index, { account_code: code })}
                                isDark={isDark}
                                placeholder="— GL account —"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <label className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <input
                                type="checkbox"
                                checked={rule.case_sensitive}
                                onChange={(e) => updateSupplierRule(index, { case_sensitive: e.target.checked })}
                                className="w-3 h-3 rounded border-gray-300"
                                style={{ accentColor: actionColor }}
                              />
                              Case sensitive
                            </label>
                            <label className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <input
                                type="checkbox"
                                checked={rule.auto_categorize}
                                onChange={(e) => updateSupplierRule(index, { auto_categorize: e.target.checked })}
                                className="w-3 h-3 rounded border-gray-300"
                                style={{ accentColor: actionColor }}
                              />
                              Auto-categorize
                            </label>
                            <div className="flex-1" />
                            <input
                              type="number"
                              value={rule.priority}
                              onChange={(e) => updateSupplierRule(index, { priority: parseInt(e.target.value) || 0 })}
                              className={`w-16 px-2 py-0.5 rounded border text-xs ${
                                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              placeholder="Priority"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* All Conditions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    All Conditions Must Match (AND)
                  </label>
                  <button
                    onClick={() => addCondition('all')}
                    className="flex items-center gap-1 text-sm hover:opacity-80"
                    style={{ color: actionColor }}
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {conditions.all.map((cond, index) => renderCondition('all', cond, index))}
                  {conditions.all.length === 0 && (
                    <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No conditions added. Click "Add" to create conditions.
                    </p>
                  )}
                </div>
              </div>

              {/* Any Conditions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    At Least One Condition Must Match (OR)
                  </label>
                  <button
                    onClick={() => addCondition('any')}
                    className="flex items-center gap-1 text-sm hover:opacity-80"
                    style={{ color: actionColor }}
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {conditions.any.map((cond, index) => renderCondition('any', cond, index))}
                  {conditions.any.length === 0 && (
                    <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No conditions added. Click "Add" to create conditions.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Actions */}
          {step === 'actions' && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Action Type *
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {actionOptions.map((action) => (
                    <button
                      key={action.value}
                      type="button"
                      onClick={() => setActionType(action.value)}
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all ${actionType === action.value
                          ? 'border-transparent'
                          : isDark
                            ? 'border-gray-600 hover:border-gray-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      style={actionType === action.value ? { borderColor: actionColor, backgroundColor: `${actionColor}14` } : undefined}
                    >
                      <div
                        className={`p-1.5 rounded ${actionType === action.value
                            ? ''
                            : isDark
                              ? 'bg-gray-700 text-gray-400'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        style={actionType === action.value ? { backgroundColor: `${actionColor}15`, color: actionColor } : undefined}
                      >
                        {action.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium ${actionType === action.value
                              ? ''
                              : isDark
                                ? 'text-gray-200'
                                : 'text-gray-700'
                            }`}
                          style={actionType === action.value ? { color: actionColor } : undefined}
                        >
                          {action.label}
                        </div>
                        <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {action.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Parameters */}
              {selectedAction && selectedAction.params.length > 0 && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Action Parameters
                  </label>
                  <div className="space-y-3">
                    {selectedAction.params.map((param) => (
                      <div key={param.key}>
                        <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {param.label}
                        </label>
                        {param.type === 'supplier' ? (
                          <InlineSupplierLookup
                            value={(() => {
                              const id = String((actionConfig as Record<string, unknown>)[param.key] || '');
                              if (!id) return null;
                              const nameKey = param.key.replace(/_id$/, '_name');
                              const name = String((actionConfig as Record<string, unknown>)[nameKey] || '') || supplierNameFor(id) || id;
                              return { id, display_name: name };
                            })()}
                            onChange={(s) => {
                              const nameKey = param.key.replace(/_id$/, '_name');
                              setActionConfig({
                                ...actionConfig,
                                [param.key]: s?.id || '',
                                [nameKey]: s?.display_name || '',
                              } as ExpensePolicyRule['action_config']);
                            }}
                            isDark={isDark}
                            placeholder="Search suppliers…"
                          />
                        ) : param.type === 'supplier_list' ? (
                          <SupplierIdListEditor
                            value={(actionConfig as Record<string, unknown>)[param.key]}
                            onChange={(ids) => setActionConfig({ ...actionConfig, [param.key]: ids } as ExpensePolicyRule['action_config'])}
                            isDark={isDark}
                            supplierNameFor={supplierNameFor}
                          />
                        ) : param.type === 'gl_account' ? (
                          <SearchableAccountSelect
                            accounts={expenseAccounts}
                            value={String((actionConfig as Record<string, unknown>)[param.key] || '')}
                            onChange={(code) => setActionConfig({ ...actionConfig, [param.key]: code } as ExpensePolicyRule['action_config'])}
                            isDark={isDark}
                            placeholder="— GL account —"
                          />
                        ) : param.type === 'select' ? (
                          <select
                            value={String((actionConfig as Record<string, unknown>)[param.key] || '')}
                            onChange={(e) => setActionConfig({ ...actionConfig, [param.key]: e.target.value } as ExpensePolicyRule['action_config'])}
                            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                              }`}
                          >
                            <option value="">Select...</option>
                            {param.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : param.type === 'textarea' ? (
                          <textarea
                            value={String((actionConfig as Record<string, unknown>)[param.key] || '')}
                            onChange={(e) => setActionConfig({ ...actionConfig, [param.key]: e.target.value } as ExpensePolicyRule['action_config'])}
                            rows={3}
                            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            placeholder={param.label}
                          />
                        ) : (
                          <input
                            type={param.type === 'number' ? 'number' : 'text'}
                            value={String((actionConfig as Record<string, unknown>)[param.key] || '')}
                            onChange={(e) => setActionConfig({ ...actionConfig, [param.key]: e.target.value } as ExpensePolicyRule['action_config'])}
                            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            placeholder={param.label}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Settings */}
          {step === 'settings' && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Priority
                </label>
                <input
                  type="number"
                  min="0"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                  className={`w-24 px-3 py-2 rounded-lg border ${isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2`}
                  style={ringFocusStyle}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Higher priority rules are evaluated first
                </p>
              </div>

              <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300"
                  style={{ accentColor: actionColor }}
                />
                <div>
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Enable policy
                  </label>
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Disable to pause this policy without deleting it
                  </p>
                </div>
              </div>

              {/* Per-User / Per-Role Targeting */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Users className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Targeting
                  </label>
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <input
                    type="checkbox"
                    checked={appliesToAll}
                    onChange={(e) => setAppliesToAll(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300"
                  style={{ accentColor: actionColor }}
                  />
                  <div>
                    <label className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Apply to all users
                    </label>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      When enabled, this policy applies to everyone. Disable to target specific users or roles.
                    </p>
                  </div>
                </div>
                {!appliesToAll && (
                  <div className="space-y-3 ml-7">
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Target Roles
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {USER_ROLE_OPTIONS.map((role) => {
                          const selected = targetRoles.split(',').map((r) => r.trim()).includes(role.value);
                          return (
                            <label
                              key={role.value}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer transition-colors ${
                                selected
                                  ? 'text-white border-transparent'
                                  : isDark
                                    ? 'border-gray-600 text-gray-300 hover:border-gray-500'
                                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                              }`}
                              style={selected ? { backgroundColor: actionColor } : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => {
                                  const current = targetRoles.split(',').map((r) => r.trim()).filter(Boolean);
                                  const next = e.target.checked
                                    ? [...current, role.value]
                                    : current.filter((r) => r !== role.value);
                                  setTargetRoles(next.join(', '));
                                }}
                                className="sr-only"
                              />
                              {role.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Target User IDs (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={targetUserIds}
                        onChange={(e) => setTargetUserIds(e.target.value)}
                        placeholder="e.g., 12, 34, 56"
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          } focus:outline-none focus:ring-2`}
                        style={ringFocusStyle}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Summary
                </h4>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <p>
                    <strong>Type:</strong> {selectedTrigger?.label || 'Not selected'}
                  </p>
                  <p className="mt-1">
                    <strong>Conditions:</strong> {conditions.all.length} ALL + {conditions.any.length} ANY
                  </p>
                  <p className="mt-1">
                    <strong>Action:</strong> {selectedAction?.label || 'Not selected'}
                  </p>
                  <p className="mt-1">
                    <strong>Priority:</strong> {priority}
                  </p>
                  <p className="mt-1">
                    <strong>Targeting:</strong> {appliesToAll ? 'All users' : [targetRoles, targetUserIds].filter(Boolean).join(', ') || 'Not specified'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Test / Simulate */}
          {step === 'test' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <FlaskConical className="w-5 h-5" style={{ color: actionColor }} />
                <h3 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Test / Simulate Policy
                </h3>
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Enter sample expense data below to test which policies (including your current draft) would match.
              </p>

              {/* Simulate input form */}
              <div className={`p-4 rounded-lg space-y-3 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={simData.amount}
                      onChange={(e) => setSimData({ ...simData, amount: Number(e.target.value) || 0 })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Currency</label>
                    <select
                      value={simData.currency || 'ZAR'}
                      onChange={(e) => setSimData({ ...simData, currency: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      {(currencyOptions.length > 0 ? currencyOptions : [{ value: 'ZAR', label: 'ZAR' }]).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Category</label>
                    <select
                      value={String(simData.category || '')}
                      onChange={(e) => setSimData({ ...simData, category: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">— None —</option>
                      {EXPENSE_CATEGORY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Supplier</label>
                    <InlineSupplierLookup
                      value={simData.vendor_id || simData.supplier_id
                        ? {
                            id: String(simData.supplier_id || simData.vendor_id),
                            display_name: String(simData.vendor || supplierNameFor(String(simData.supplier_id || simData.vendor_id)) || ''),
                          }
                        : null}
                      onChange={(s) => setSimData({
                        ...simData,
                        vendor_id: s ? Number(s.id) : undefined,
                        supplier_id: s?.id || '',
                        vendor: s?.display_name || '',
                      })}
                      isDark={isDark}
                      placeholder="Search suppliers…"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vendor Name (extracted text)</label>
                    <input
                      type="text"
                      value={simData.vendor || ''}
                      onChange={(e) => setSimData({ ...simData, vendor: e.target.value })}
                      placeholder="e.g. SHELL GARAGE"
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment Method</label>
                    <select
                      value={simData.payment_method || ''}
                      onChange={(e) => setSimData({ ...simData, payment_method: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">— None —</option>
                      {EXPENSE_PAYMENT_METHOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Expense Type</label>
                    <select
                      value={simData.expense_type || ''}
                      onChange={(e) => setSimData({ ...simData, expense_type: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">— None —</option>
                      {EXPENSE_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Expense Subtype</label>
                    <input
                      type="text"
                      list="expense-subtype-suggestions"
                      value={simData.expense_subtype || ''}
                      onChange={(e) => setSimData({ ...simData, expense_subtype: e.target.value })}
                      placeholder="fuel, toll, travel, etc."
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                    <datalist id="expense-subtype-suggestions">
                      {EXPENSE_SUBTYPE_SUGGESTIONS.map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Expense Date</label>
                    <input
                      type="date"
                      value={simData.expense_date || ''}
                      onChange={(e) => setSimData({ ...simData, expense_date: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Receipt Amount</label>
                    <input
                      type="number"
                      value={simData.receipt_amount || 0}
                      onChange={(e) => setSimData({ ...simData, receipt_amount: Number(e.target.value) || 0 })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Receipt Number</label>
                    <input
                      type="text"
                      value={simData.receipt_number || ''}
                      onChange={(e) => setSimData({ ...simData, receipt_number: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <div className="flex items-center gap-3 col-span-2">
                    <input
                      type="checkbox"
                      checked={simData.has_receipt ?? true}
                      onChange={(e) => setSimData({ ...simData, has_receipt: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300"
                      style={{ accentColor: actionColor }}
                    />
                    <label className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Has Receipt</label>
                  </div>
                </div>
                <button
                  onClick={runSimulation}
                  disabled={simRunning}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: actionColor }}
                >
                  {simRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {simRunning ? 'Running...' : 'Run Simulation'}
                </button>
              </div>

              {/* Simulation Results */}
              {simResults && (
                <div className="space-y-2">
                  <h4 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Results ({simResults.length} policies evaluated)
                  </h4>
                  {simResults.length === 0 ? (
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No policies to evaluate.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {simResults.map((res, i) => (
                        <div
                          key={i}
                          className={`p-3 rounded-lg border ${res.wouldMatch
                              ? isDark
                                ? 'border-amber-600 bg-amber-900/20'
                                : 'border-amber-300 bg-amber-50'
                              : isDark
                                ? 'border-gray-700 bg-gray-800'
                                : 'border-gray-200 bg-white'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              {res.policyName}
                            </span>
                            {res.wouldMatch ? (
                              <span className="text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                Matched
                              </span>
                            ) : (
                              <span className="text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                No match
                              </span>
                            )}
                          </div>
                          {res.wouldMatch && (
                            <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span><strong>Action:</strong> {res.action}</span>
                              {res.message && <span className="ml-2">— {res.message}</span>}
                              <span className="ml-2">({res.matchedConditions} conditions matched)</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
    </SlideOverShell>
  );
}
